/**
 * Content masking for the third-party capture tools that read rendered DOM text.
 *
 * Every signed-in screen in this app renders a client's books — ledger rows,
 * bank feed descriptors, account and vendor names, amounts, client company
 * names. Hotjar, Heap and PostHog autocapture all read on-screen TEXT, not just
 * input values, so stock defaults ship those books to three vendors. The
 * Security page promises customer data is not replicated outside us-central1.
 *
 * 🪤 This is applied to `document.body`, NOT to a React container, and that is
 * deliberate. AntD renders every Modal, Drawer, Select dropdown and Tooltip
 * through a portal appended to `document.body`, so they are OUTSIDE the React
 * tree under <main>. The transaction-detail modal and the account pickers are
 * exactly the surfaces with the most ledger text on them — a wrapper around
 * <main> would mask the table behind the modal and miss the modal itself.
 *
 * 🪤 It is also NOT applied per table or per field. `<Table` appears in 119
 * files; a masking attribute added table by table fails silently the first time
 * someone adds the 120th, and a silent failure here means a client's ledger
 * leaves the browser. One container that is impossible to render outside of is
 * the only version of this that stays true.
 *
 * All three attributes cover the tagged element AND its descendants:
 *   - Hotjar   `data-hj-suppress` — suppresses all text/image content inside.
 *   - PostHog  `ph-no-capture` class — masking applies to children.
 *   - Heap     `data-heap-redact-text` — descendant behaviour is NOT documented
 *              for the web SDK, so this is belt only. Heap's certain control is
 *              the global `disableTextCapture` client option, set in heap.ts.
 */

/** Hotjar: suppress text, images and video inside this element and below it. */
const HOTJAR_SUPPRESS_ATTRIBUTE = "data-hj-suppress";

/** Heap: redact target text. Belt; `disableTextCapture` in heap.ts is the brace. */
const HEAP_REDACT_ATTRIBUTE = "data-heap-redact-text";

/** PostHog: block autocapture and mask replay text for this subtree. */
const POSTHOG_NO_CAPTURE_CLASS = "ph-no-capture";

/**
 * Mask the whole document for the DOM-reading capture tools. Idempotent, so it
 * is safe to call on every mount.
 */
export const applyPrivacyMask = () => {
	const { body } = document;
	if (!body) return;

	body.setAttribute(HOTJAR_SUPPRESS_ATTRIBUTE, "");
	body.setAttribute(HEAP_REDACT_ATTRIBUTE, "true");
	body.classList.add(POSTHOG_NO_CAPTURE_CLASS);
};

/**
 * Remove the mask. Called when the authenticated shell unmounts (sign-out), so
 * the unauthenticated funnel pages — which carry no client data and are where
 * the signup instrumentation lives — are not masked for the rest of the tab's
 * life.
 */
export const clearPrivacyMask = () => {
	const { body } = document;
	if (!body) return;

	body.removeAttribute(HOTJAR_SUPPRESS_ATTRIBUTE);
	body.removeAttribute(HEAP_REDACT_ATTRIBUTE);
	body.classList.remove(POSTHOG_NO_CAPTURE_CLASS);
};

/** Exported for the test only, so the attribute names cannot drift silently. */
export const PRIVACY_MASK = {
	hotjarAttribute: HOTJAR_SUPPRESS_ATTRIBUTE,
	heapAttribute: HEAP_REDACT_ATTRIBUTE,
	posthogClass: POSTHOG_NO_CAPTURE_CLASS,
} as const;
